import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
  Typography,
  MenuItem,
  Alert,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { getUniqueCurrencies } from "../../utils/currencyOptions";

const SalePaymentAdd = ({ open, onClose, token, onSuccess }) => {
  const { t } = useTranslation();
  const { handleAdd, loading, resetForm } = useAdd(
    "shop/customer-balance-payments",
    token
  );

  const { data: customersData, loading: customersLoading } = useFetchObjects(
    ["customers", "sale-payment-add"],
    "shop/customers?page_size=all",
    token
  );
  const { data: salesData } = useFetchObjects(
    ["sales", "sale-payment-add"],
    "shop/sales/?page_size=all",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency", "sale-payment-add"],
    "shop/currencies?page_size=all",
    token
  );

  const customers = customersData?.results || customersData || [];
  const sales = salesData?.results || salesData || [];
  const currencies = getUniqueCurrencies(currenciesData?.results || currenciesData || []);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().slice(0, 16)
  );
  const [notes, setNotes] = useState("");
  const customerOptions = useMemo(() => {
    const base = [...(customers || [])];
    const virtuals = [];
    const seen = new Set(base.map((c) => (c.name || "").trim().toLowerCase()).filter(Boolean));
    (sales || []).forEach((s) => {
      if (!s?.customer && (s?.general_customer_name || s?.general_customer_phone)) {
        const name = (s.general_customer_name || "General Customer").trim();
        const phone = (s.general_customer_phone || "").trim();
        const key = name.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          virtuals.push({
            id: `general-customer::${name}::${phone}`,
            name,
          });
        }
      }
    });
    return [...virtuals, ...base];
  }, [customers, sales]);

  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "sale-payment-add"],
    "shop/accounts/?page_size=all",
    token
  );
  const accounts = Array.isArray(accountsData)
    ? accountsData
    : accountsData?.results || [];

  const selectedCurrencyId = selectedCurrency?.id ? String(selectedCurrency.id) : "";
  const shopAccounts = useMemo(() => accounts, [accounts]);
  const sameCurrencyAccounts = useMemo(
    () => accounts.filter((a) => String(a.currency) === String(selectedCurrencyId)),
    [accounts, selectedCurrencyId]
  );
  const selectedAccount = useMemo(
    () =>
      shopAccounts.find((a) => String(a.id) === String(selectedAccountId)) ||
      null,
    [shopAccounts, selectedAccountId]
  );

  useEffect(() => {
    if (!selectedCurrencyId) {
      setSelectedAccountId("");
      return;
    }
    if (shopAccounts.length === 0) {
      setSelectedAccountId("");
      return;
    }
    const exists = shopAccounts.some((a) => String(a.id) === String(selectedAccountId));
    if (!exists) {
      const preferred = sameCurrencyAccounts[0] || shopAccounts[0];
      setSelectedAccountId(preferred ? String(preferred.id) : "");
    }
  }, [selectedCurrencyId, shopAccounts, sameCurrencyAccounts, selectedAccountId]);

  const { data: directRateData } = useFetchObjects(
    [
      "sale-payment-rate-direct",
      selectedCurrencyId,
      selectedAccount?.currency,
    ],
    selectedCurrencyId && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&from_currency=${selectedCurrencyId}&to_currency=${selectedAccount.currency}`
      : "",
    token
  );
  const { data: reverseRateData } = useFetchObjects(
    [
      "sale-payment-rate-reverse",
      selectedCurrencyId,
      selectedAccount?.currency,
    ],
    selectedCurrencyId && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&from_currency=${selectedAccount.currency}&to_currency=${selectedCurrencyId}`
      : "",
    token
  );

  const exchangeRatePreview = useMemo(() => {
    if (!selectedCurrencyId) return "";
    const accountCurrencyId = selectedAccount?.currency ?? selectedCurrencyId;
    if (String(selectedCurrencyId) === String(accountCurrencyId)) return "1";
    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  }, [selectedCurrencyId, selectedAccount, directRateData, reverseRateData]);

  const convertedAmountPreview = useMemo(() => {
    const nAmount = Number(amount);
    const nRate = Number(exchangeRate);
    if (
      !Number.isFinite(nAmount) ||
      !Number.isFinite(nRate) ||
      nAmount <= 0 ||
      nRate <= 0
    ) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  }, [amount, exchangeRate]);

  useEffect(() => {
    setExchangeRate(exchangeRatePreview);
  }, [exchangeRatePreview]);

  const handleSubmit = () => {
    if (
      !selectedCustomer?.id ||
      !selectedCurrency?.id ||
      !amount ||
      !exchangeRate
    )
      return;
    const payload = {
      customer: selectedCustomer.id,
      currency: selectedCurrency.id,
      amount: Number(amount),
      exchange_rate: exchangeRate,
      payment_date: paymentDate,
      notes: notes.trim() || undefined,
    };
    if (selectedAccountId) payload.account = selectedAccountId;
    handleAdd(payload);
  };

  useEffect(() => {
    if (resetForm) {
      setSelectedCustomer(null);
      setSelectedCurrency(null);
      setSelectedAccountId("");
      setAmount("");
      setExchangeRate("");
      setPaymentDate(new Date().toISOString().slice(0, 16));
      setNotes("");
      onSuccess?.();
      onClose();
    }
  }, [resetForm, onClose, onSuccess]);

  const currentBalance = selectedCustomer?.total_owed_by_currency;
  const balanceStr =
    currentBalance && typeof currentBalance === "object"
      ? Object.entries(currentBalance)
          .map(([cur, amt]) => `${Number(amt).toFixed(2)} ${cur}`)
          .join(", ") || "0"
      : "0";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t("salePayment.recordPayment", "Customer payment")}
      </DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <Autocomplete
            value={selectedCustomer}
            onChange={(e, newValue) => setSelectedCustomer(newValue)}
            options={customerOptions}
            getOptionLabel={(option) => option.name || "—"}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            loading={customersLoading}
            disabled={false}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("salePayment.customer", "Customer")}
                margin="dense"
                fullWidth
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {customersLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          {selectedCustomer && (
            <Box mt={1} p={2} bgcolor="grey.100" borderRadius={1}>
              <Typography variant="body2" color="textSecondary">
                {t("salePayment.currentBalance", "Current balance")}:{" "}
                <strong>{balanceStr}</strong>
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block">
                {t("salePayment.balanceReducedByPayment", "Payment will reduce this balance.")}
              </Typography>
            </Box>
          )}

          <Autocomplete
            value={selectedCurrency}
            onChange={(e, newValue) => {
              setSelectedCurrency(newValue);
              setSelectedAccountId("");
            }}
            options={currencies || []}
            getOptionLabel={(option) => option.title || ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loading={currencyLoading}
            disabled={currencyLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("salePayment.selectCurrency", "Currency")}
                margin="dense"
                fullWidth
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {currencyLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <TextField
            select
            margin="dense"
            label={t("salePayment.account", "Account")}
            fullWidth
            required
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            disabled={!selectedCurrencyId || accountLoading}
          >
            {accountLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : shopAccounts.length === 0 ? (
              <MenuItem value="" disabled>
                {t(
                  "salePayment.noAccount",
                  "No accounts found"
                )}
              </MenuItem>
            ) : (
              shopAccounts.map((acc) => (
                <MenuItem key={acc.id} value={String(acc.id)}>
                  {acc.name} ({acc.currency_title || "-"})
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("salePayment.amount", "Amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
          />

          <TextField
            margin="dense"
            label={t("salePayment.exchangeRate", "Exchange Rate")}
            type="number"
            fullWidth
            required
            value={exchangeRate}
            onChange={(e) => setExchangeRate(e.target.value)}
            inputProps={{ min: 0, step: "0.000001" }}
          />

          <TextField
            margin="dense"
            label={t("salePayment.convertedAmount", "Converted Amount")}
            fullWidth
            value={convertedAmountPreview}
            disabled
          />

          {!exchangeRatePreview && selectedCurrencyId && selectedAccountId && !exchangeRate && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t(
                "salePayment.missingRate",
                "No exchange rate found for selected account currency. You can enter it manually."
              )}
            </Alert>
          )}
          <TextField
            margin="dense"
            label={t("salePayment.paymentDate", "Payment date")}
            type="datetime-local"
            fullWidth
            required
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("salePayment.notes", "Notes")}
            multiline
            rows={2}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("salePayment.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !selectedCustomer ||
            !selectedCurrency ||
            !amount ||
            !exchangeRate
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("salePayment.submit", "Customer payment")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SalePaymentAdd;
